/**
 * Data sources (all backend HTTP → Mongo/Langfuse/etc. behind the API):
 * - Health cards: GET /health, GET /api/runs (heuristic), GET /api/datasets
 * - Agent table: GET /api/agents (validation_* fields stored on agent documents)
 * - Scheduler table: GET /api/runs?source=scheduler (run rows in DB)
 */
import React from "react";
import { Link } from "react-router-dom";
import styles from "../components/Dashboard.module.css";
import {
  diagnosticsApi,
  HealthState,
  HealthRow,
  AgentValidation,
  SchedulerOutcome,
} from "../services/diagnosticsApi";

const stateLabel = (state: HealthState) =>
  state === "pass" ? "Pass" : state === "warn" ? "Warning" : "Fail";

const stateIcon = (state: HealthState) => (state === "pass" ? "✓" : state === "warn" ? "!" : "x");

const DiagnosticsPage: React.FC = () => {
  const [healthRows, setHealthRows] = React.useState<HealthRow[]>([]);
  const [agentOptions, setAgentOptions] = React.useState<Array<{ id: string; name: string }>>([]);
  const [selectedAgentId, setSelectedAgentId] = React.useState("");
  const [agentValidations, setAgentValidations] = React.useState<AgentValidation[]>([]);
  const [schedulerOutcomes, setSchedulerOutcomes] = React.useState<SchedulerOutcome[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [agentLoading, setAgentLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [health, options, sched] = await Promise.all([
          diagnosticsApi.getSystemHealth(),
          diagnosticsApi.listAgentOptions(),
          diagnosticsApi.getSchedulerOutcomes(),
        ]);
        if (!mounted) return;
        setHealthRows(health);
        setAgentOptions(options);
        setSchedulerOutcomes(sched);
        const selected = options[0]?.id || "";
        setSelectedAgentId(selected);
        const validations = await diagnosticsApi.getAgentValidations(selected || undefined);
        if (!mounted) return;
        setAgentValidations(validations);
      } catch {
        if (!mounted) return;
        setError("Failed to load diagnostics.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  const onChangeAgent = async (nextId: string) => {
    setSelectedAgentId(nextId);
    setAgentLoading(true);
    setError(null);
    try {
      const validations = await diagnosticsApi.getAgentValidations(nextId || undefined);
      setAgentValidations(validations);
    } catch {
      setError("Failed to load diagnostics.");
    } finally {
      setAgentLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.dashboard}>
        <section className={styles.dashboardLoading} aria-live="polite" aria-busy="true">
          <div className={styles.dashboardLoadingSpinner} />
          <p className={styles.dashboardLoadingText}>Loading diagnostics...</p>
        </section>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.diagnosticsPage}>
        <section className={styles.metricsErrorBox}>
          <p>{error}</p>
        </section>
      </div>
    );
  }

  return (
    <div className={styles.diagnosticsPage}>
      {/*
        Operator-facing doc banner (FE-07 / Swagger pointers) — restore if needed:
        <p className={styles.diagnosticsDocBanner}>
          <strong>FE-07 (diagnostics)</strong> — signals from GET /health, GET /api/runs,
          GET /api/datasets, GET /api/agents. No single GET /api/diagnostics route.
        </p>
      */}

      <section className={styles.diagnosticsCard}>
        <h1 className={styles.diagnosticsTitle}>Diagnostics</h1>
        <p className={styles.diagnosticsSubtitle}>
          Readiness snapshot for operators. This v1 view is intentionally read-only.
        </p>
        <div className={styles.diagnosticsHealthGrid}>
          {healthRows.map((row) => (
            <div
              key={row.id}
              className={`${styles.diagnosticsHealthItem} ${
                row.state === "pass"
                  ? styles.diagnosticsHealthItemPass
                  : row.state === "warn"
                  ? styles.diagnosticsHealthItemWarn
                  : styles.diagnosticsHealthItemFail
              } ${row.indirect ? styles.diagnosticsHealthItemLimited : ""}`.trim()}
            >
              <div className={styles.diagnosticsMetricCardTop}>
                <span className={styles.diagnosticsMetricIcon}>{stateIcon(row.state)}</span>
              </div>
              <strong className={styles.diagnosticsMetricValue}>{stateLabel(row.state)}</strong>
              <p className={styles.diagnosticsMetricTitle}>
                {row.label}
                {row.indirect ? (
                  <span className={styles.diagnosticsIndirectBadge}>Indirect</span>
                ) : null}
              </p>
              <p className={styles.diagnosticsMetricDelta}>{row.message}</p>
            </div>
          ))}
        </div>
      </section>

      <section className={styles.diagnosticsCard}>
        <h2 className={styles.diagnosticsSectionTitle}>Selected agent validation results</h2>
        {/*
          Hint: validation rows come from GET /api/agents; re-validate via Agents tab.
        */}
        <div className={styles.metricsPairRow}>
          <select
            id="diagnosticsAgentSelect"
            className={`${styles.metricsSelect} ${styles.diagnosticsAgentSelect}`.trim()}
            data-testid="agent-select"
            value={selectedAgentId}
            onChange={(e) => onChangeAgent(e.target.value)}
          >
            <option value="">All agents</option>
            {agentOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.name}
              </option>
            ))}
          </select>
          {agentLoading ? (
            <div className={styles.diagnosticsInlineLoader} aria-live="polite" aria-busy="true">
              <div className={styles.dashboardLoadingSpinner} />
            </div>
          ) : null}
        </div>
        <div className={styles.diagnosticsTableWrap}>
          <table className={styles.diagnosticsTable}>
            <thead>
              <tr>
                <th>Agent</th>
                <th>Status</th>
                <th>Summary</th>
                <th>Checked at</th>
              </tr>
            </thead>
            <tbody>
              {agentValidations.map((row) => (
                <tr key={row.agentId}>
                  <td>{row.agentName}</td>
                  <td>{stateLabel(row.state)}</td>
                  <td>{row.summary}</td>
                  <td>{new Date(row.checkedAt).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className={styles.diagnosticsCard}>
        <h2 className={styles.diagnosticsSectionTitle}>Recent scheduler-originated runs</h2>
        <p className={styles.diagnosticsSectionHint}>
          Run rows from <code>GET /api/runs?source=scheduler</code>. Full pass reports and admin actions:{" "}
          <Link to="/scheduler">Scheduler</Link>.
        </p>
        <div className={styles.diagnosticsTableWrap}>
          <table className={styles.diagnosticsTable}>
            <thead>
              <tr>
                <th>Pair</th>
                <th>Outcome</th>
                <th>Reason</th>
                <th>Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {schedulerOutcomes.length > 0 ? (
                schedulerOutcomes.map((row) => (
                  <tr key={row.id}>
                    <td>{row.pair}</td>
                    <td>
                      {row.outcome === "in_progress"
                        ? "In progress"
                        : row.outcome === "ran"
                          ? "Completed"
                          : row.outcome === "skipped"
                            ? "Skipped"
                            : "Failed"}
                    </td>
                    <td>{row.reason}</td>
                    <td>{new Date(row.at).toLocaleString()}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4}>No recent issues.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/*
        FE-07 “gaps” explainer (no GET /api/diagnostics, no worker heartbeat route, etc.) — restore when Product wants it visible:
        <section className={`${styles.metricsCard} ${styles.metricsUnsupportedCard}`} aria-disabled="true">...</section>
      */}
    </div>
  );
};

export default DiagnosticsPage;
